import { useNavigate } from "react-router-dom";
import { Wallet, CalendarClock, Zap, TrendingUp, IdCard, ShieldCheck, FileText } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import Footer from "@/components/Footer";
import { PrimaryButton, SecondaryButton } from "@/components/brand";

const HERO_IMG = "https://images.unsplash.com/photo-1589923188900-85dae523342b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const VALUE_PROPS = [
  { icon: Wallet, title: "Transparent pay per delivery", body: "See what you earn on every job before you accept it. Clear rates, no guessing." },
  { icon: CalendarClock, title: "A schedule that fits you", body: "Drive when it works for you. Turn on your shift, share your location, accept the runs you want." },
  { icon: Zap, title: "Fast verification", body: "Apply once and get reviewed by a real operations team, usually within 48 hours." },
  { icon: TrendingUp, title: "AI routing means more runs", body: "Routa's smart routing keeps you moving efficiently between stops, so your time on the road turns into more completed deliveries." },
];

const STEPS = [
  { n: "01", title: "Apply to drive", body: "Fill out one application with your details, licence, vehicle, and insurance." },
  { n: "02", title: "Get verified", body: "We review your documents, licensing, and insurance against provincial standards." },
  { n: "03", title: "Start accepting deliveries", body: "Once approved, your dashboard goes live and AI-routed orders start flowing." },
];

const REQUIREMENTS = [
  ["Valid BC driver's licence", "Class 5, Class 4, or commercial class accepted"],
  ["Vehicle insurance", "Minimum $2,000,000 liability with a commercial-use endorsement or equivalent"],
  ["Vehicle registration", "Current registration for the vehicle you will operate"],
  ["Background consent", "Canadian Criminal Record Check and a current driver abstract, both under 90 days old"],
];

export default function DriverLanding() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-4 mb-8">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">[01] For Drivers</span>
              <span className="h-px w-16 bg-gray-300" />
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Vancouver · Canada</span>
            </div>
            <h1 className="headline text-5xl lg:text-[4.5rem] text-[#0a0a0a]">
              Flexible income. Real <span className="text-orange-500">credibility.</span>
            </h1>
            <p className="mt-7 text-base text-gray-500 max-w-md leading-relaxed">
              Being verified is what makes you hireable. Join a professional network of BC drivers, get transparent pay, AI-routed runs, and drive on your own schedule.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton orange arrow data-testid="driver-hero-cta" onClick={() => navigate("/apply")}>
                Apply to Drive
              </PrimaryButton>
              <SecondaryButton data-testid="driver-hero-login" onClick={() => navigate("/login?role=driver")}>
                Driver login
              </SecondaryButton>
            </div>
          </div>

          <div className="relative aspect-[4/5] w-full overflow-hidden animate-fade-up" style={{ animationDelay: "120ms" }}>
            <img src={HERO_IMG} alt="Professional driver" className="absolute inset-0 h-full w-full object-cover grayscale" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/85 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/60">Operator Node</div>
              <div className="font-mono text-sm text-white mt-1">Verified. Professional. Trusted.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="bg-gray-100 py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-10">
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">[02] Why Drive With Routa</span>
            <span className="h-px flex-1 bg-gray-200" />
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Professional operators</span>
          </div>
          <div className="grid md:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="bg-white p-8">
                <v.icon className="h-6 w-6 text-orange-500" strokeWidth={1.8} />
                <h3 className="mt-6 text-xl font-bold text-[#0a0a0a]">{v.title}</h3>
                <p className="mt-3 text-sm text-gray-500 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20">
        <div className="flex items-center gap-4 mb-12">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">[03] How It Works</span>
          <span className="h-px flex-1 bg-gray-200" />
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Three steps</span>
        </div>
        <div className="grid md:grid-cols-3 gap-10">
          {STEPS.map((s) => (
            <div key={s.n}>
              <div className="font-mono text-sm text-orange-500 mb-4">{s.n}</div>
              <h3 className="text-xl font-bold text-[#0a0a0a]">{s.title}</h3>
              <p className="mt-3 text-sm text-gray-500 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Requirements */}
      <section className="bg-gray-100 py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="flex items-center gap-4 mb-10">
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">[04] What You Need</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="headline text-3xl lg:text-4xl text-[#0a0a0a]">Have these ready before you apply.</h2>
              <p className="mt-5 text-base text-gray-500 max-w-md leading-relaxed">
                A short checklist keeps verification fast. Get these together and you can be reviewed within 48 hours.
              </p>            </div>
            <div className="border border-gray-200 bg-white divide-y divide-gray-200">
              {REQUIREMENTS.map(([k, v], i) => {
                const Icon = [IdCard, ShieldCheck, FileText, ShieldCheck][i] || FileText;
                return (
                  <div key={k} className="flex items-start gap-3 px-5 py-4">
                    <Icon className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm text-[#0a0a0a]">{k}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{v}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#0a0a0a] py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
          <div>
            <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/40 mb-5">[05] Get Started</div>
            <h2 className="headline text-3xl lg:text-5xl text-white">Apply once. Get reviewed within 48 hours.</h2>
          </div>
          <PrimaryButton orange arrow data-testid="driver-closing-cta" onClick={() => navigate("/apply")} className="shrink-0">
            Apply to Drive
          </PrimaryButton>
        </div>
      </section>

      <Footer />
    </div>
  );
}
